import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { ESN_SIDEBAR_DRAWER_QUERY, type EsnSidebarMode } from '../../../shared/types/shell';
import { AvatarComponent } from '../../atoms/avatar/avatar.component';
import { IconButtonComponent } from '../../atoms/icon-button/icon-button.component';
import { LogoComponent } from '../../atoms/logo/logo.component';
import { SwitchComponent } from '../../atoms/switch/switch.component';

let nextId = 0;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * The console's navigation column: brand, signed-in account, the nav entries the page projects and
 * a footer with the theme switch. On wide screens it is a 260px rail; below 1024px the same column
 * becomes a drawer over a scrim, opened by a 44px toggle, closed by Escape, the scrim or a link.
 */
@Component({
  selector: 'esn-sidebar',
  imports: [NgTemplateOutlet, AvatarComponent, IconButtonComponent, LogoComponent, SwitchComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.esn-sidebar-host--drawer]': 'isDrawer()',
    '(document:keydown.escape)': 'handleEscape()',
  },
})
export class SidebarComponent {
  readonly mode = input<EsnSidebarMode>('auto');

  /**
   * Drawer open state; ignored while the sidebar is a rail.
   */
  readonly open = model(false);

  /**
   * Accessible name of the navigation landmark (and of the drawer dialog).
   */
  readonly label = input('Main navigation');

  /**
   * Signed-in person shown under the logo; omit to hide the account row.
   */
  readonly userName = input<string | null>(null);
  readonly userImageUrl = input<string | null>(null);

  /**
   * Render the account row as a button (e.g. to open an account switcher) that emits `accountClicked`.
   */
  readonly accountInteractive = input(false);

  /**
   * Accessible name of the footer theme switch; null hides the switch.
   */
  readonly themeSwitchLabel = input<string | null>(null);
  readonly lightTheme = model(false);

  /**
   * Render the built-in 44px drawer toggle; turn off to place your own and drive `open`.
   */
  readonly showToggle = input(true);
  readonly toggleLabel = input('Open navigation');

  /**
   * Close the drawer when a link inside it is followed.
   */
  readonly closeOnNavigate = input(true);

  readonly accountClicked = output<MouseEvent>();

  private readonly document = inject(DOCUMENT);
  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');
  private readonly narrow = signal(false);
  private returnFocus: HTMLElement | null = null;

  protected readonly panelId = `esn-sidebar-${nextId++}`;
  protected readonly isDrawer = computed(() => this.mode() === 'drawer' || (this.mode() === 'auto' && this.narrow()));
  protected readonly drawerOpen = computed(() => this.isDrawer() && this.open());

  constructor() {
    const media = this.document.defaultView?.matchMedia?.(ESN_SIDEBAR_DRAWER_QUERY);
    if (media) {
      this.narrow.set(media.matches);
      const listener = (event: MediaQueryListEvent) => this.narrow.set(event.matches);
      media.addEventListener('change', listener);
      inject(DestroyRef).onDestroy(() => media.removeEventListener('change', listener));
    }

    // Opening moves focus into the drawer; closing hands it back to where it came from.
    effect(() => {
      if (this.drawerOpen()) {
        this.returnFocus = this.document.activeElement as HTMLElement | null;
        setTimeout(() => this.focusables()[0]?.focus());
      } else if (this.returnFocus) {
        const target = this.returnFocus;
        this.returnFocus = null;
        setTimeout(() => target.focus?.());
      }
    });
  }

  protected close(): void {
    this.open.set(false);
  }

  protected handleEscape(): void {
    if (this.drawerOpen()) this.close();
  }

  /**
   * Keeps Tab cycling inside the open drawer, as a modal dialog should.
   */
  protected handleKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || !this.drawerOpen()) return;
    const items = this.focusables();
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = this.document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  protected handlePanelClick(event: MouseEvent): void {
    if (!this.drawerOpen() || !this.closeOnNavigate()) return;
    if ((event.target as HTMLElement | null)?.closest('a[href]')) this.close();
  }

  private focusables(): HTMLElement[] {
    return Array.from(this.panel().nativeElement.querySelectorAll<HTMLElement>(FOCUSABLE));
  }
}
